import React, { useState, useEffect, useCallback } from "react";
import { Container, Box, Tabs, Tab, Typography, Paper, Grid, Card, CardContent, CardActions, Button, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel, Tooltip, CircularProgress, Alert, Divider, Slider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { Brain, Plus, Trash2, Edit2, Play, Key, Cpu, ListTodo, RefreshCw, CheckCircle, XCircle, AlertCircle, Eye, EyeOff, ExternalLink, Zap } from "lucide-react";
import TitleBox from "../Shared/TitleBox";
import { getAIProviders, createAIProvider, updateAIProvider, deleteAIProvider, getAIAgents, createAIAgent, updateAIAgent, deleteAIAgent, testAIAgent, getMyAIKeys, upsertMyAIKey, deleteMyAIKey, testMyAIKey, getAITaskTypes, assignAIAgent } from "../../api/index";

// ─── Helpers ──────────────────────────────────────────────────────────────────
import { useTranslation } from "react-i18next";
const PROVIDER_TYPES = ["openai-compatible", "anthropic", "google", "ollama", "ocr", "custom"];
const STATUS_ICON = {
  ok: <CheckCircle size={16} color="#4caf50" />,
  failed: <XCircle size={16} color="#f44336" />,
  untested: <AlertCircle size={16} color="#ff9800" />
};
function useAsync(fn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fn();
      setData(result?.data?.data ?? result?.data ?? result);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    reload();
  }, [reload]);
  return {
    data,
    loading,
    error,
    reload
  };
}

// ─── Providers Tab ────────────────────────────────────────────────────────────

function ProvidersTab() {
  const {
    t
  } = useTranslation("ai_settings");
  const {
    data: providers,
    loading,
    reload
  } = useAsync(getAIProviders);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    slug: "",
    baseUrl: "",
    type: "openai-compatible",
    description: "",
    websiteUrl: ""
  });
  const [saving, setSaving] = useState(false);
  const openNew = () => {
    setEditing(null);
    setForm({
      name: "",
      slug: "",
      baseUrl: "",
      type: "openai-compatible",
      description: "",
      websiteUrl: ""
    });
    setOpen(true);
  };
  const openEdit = p => {
    setEditing(p);
    setForm({
      name: p.name,
      slug: p.slug,
      baseUrl: p.baseUrl,
      type: p.type,
      description: p.description || "",
      websiteUrl: p.websiteUrl || ""
    });
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      if (editing) await updateAIProvider(editing._id, form);else await createAIProvider(form);
      setOpen(false);
      reload();
    } catch (e) {
      alert(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const remove = async id => {
    if (!window.confirm(t("Delete this provider?"))) return;
    await deleteAIProvider(id);
    reload();
  };
  if (loading) return <Box sx={{
    textAlign: "center",
    py: 6
  }}><CircularProgress /></Box>;
  return <Box>
      <Box sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 3
    }}>
        <Typography variant="h6">{t("AI Providers")}</Typography>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={openNew}>{t("Add Provider")}</Button>
      </Box>

      <Grid container spacing={2}>
        {(providers || []).map(p => <Grid item xs={12} sm={6} md={4} key={p._id}>
            <Card variant="outlined" sx={{
          height: "100%"
        }}>
              <CardContent>
                <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1
            }}>
                  <Typography variant="subtitle1" fontWeight={600}>{p.name}</Typography>
                  <Box sx={{
                display: "flex",
                gap: 0.5
              }}>
                    {p.isSeeded && <Chip label={t("Built-in")} size="small" color="primary" variant="outlined" />}
                    <Chip label={p.isEnabled ? "Enabled" : "Disabled"} size="small" color={p.isEnabled ? "success" : "default"} />
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{
              display: "block",
              mb: 1
            }}>{p.type}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>{p.baseUrl}</Typography>
                {p.description && <Typography variant="body2" color="text.secondary" sx={{
              mt: 1
            }}>{p.description}</Typography>}
                <Box sx={{
              mt: 1.5,
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5
            }}>
                  {(p.models || []).slice(0, 3).map(m => <Chip key={m.id} label={m.name} size="small" variant="outlined" />)}
                  {p.models?.length > 3 && <Chip label={`+${p.models.length - 3} more`} size="small" />}
                </Box>
              </CardContent>
              <CardActions>
                <IconButton size="small" onClick={() => openEdit(p)}><Edit2 size={16} /></IconButton>
                {!p.isSeeded && <IconButton size="small" color="error" onClick={() => remove(p._id)}><Trash2 size={16} /></IconButton>}
                {p.websiteUrl && <IconButton size="small" component="a" href={p.websiteUrl} target="_blank"><ExternalLink size={16} /></IconButton>}
              </CardActions>
            </Card>
          </Grid>)}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? t("Edit Provider") : t("Add Provider")}</DialogTitle>
        <DialogContent sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pt: 2
      }}>
          <TextField label={t("Name")} value={form.name} onChange={e => setForm({
          ...form,
          name: e.target.value
        })} fullWidth />
          <TextField label={t("Slug (unique ID)")} value={form.slug} onChange={e => setForm({
          ...form,
          slug: e.target.value
        })} fullWidth disabled={!!editing} />
          <TextField label={t("Base URL")} value={form.baseUrl} onChange={e => setForm({
          ...form,
          baseUrl: e.target.value
        })} fullWidth />
          <FormControl fullWidth>
            <InputLabel>{t("Type")}</InputLabel>
            <Select value={form.type} onChange={e => setForm({
            ...form,
            type: e.target.value
          })} label={t("Type")}>
              {PROVIDER_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label={t("Description")} value={form.description} onChange={e => setForm({
          ...form,
          description: e.target.value
        })} fullWidth multiline rows={2} />
          <TextField label={t("Website URL")} value={form.websiteUrl} onChange={e => setForm({
          ...form,
          websiteUrl: e.target.value
        })} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
}

// ─── My Keys Tab ─────────────────────────────────────────────────────────────

function MyKeysTab() {
  const {
    t
  } = useTranslation("ai_settings");
  const {
    data: providers,
    loading: loadingP
  } = useAsync(getAIProviders);
  const {
    data: keys,
    loading: loadingK,
    reload
  } = useAsync(getMyAIKeys);
  const [open, setOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [keyValue, setKeyValue] = useState("");
  const [customBaseUrl, setCustomBaseUrl] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const keyMap = {};
  (keys || []).forEach(k => {
    keyMap[k.providerId] = k;
  });
  const openKeyDialog = provider => {
    setSelectedProvider(provider);
    setKeyValue("");
    setCustomBaseUrl(keyMap[provider._id]?.customBaseUrl || "");
    setShowKey(false);
    setOpen(true);
  };
  const saveKey = async () => {
    if (!keyValue.trim()) return;
    setSaving(true);
    try {
      await upsertMyAIKey(selectedProvider._id, {
        apiKey: keyValue,
        customBaseUrl
      });
      setOpen(false);
      reload();
    } catch (e) {
      alert(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const removeKey = async providerId => {
    await deleteMyAIKey(providerId);
    reload();
  };
  const testKey = async providerId => {
    setTesting(t => ({
      ...t,
      [providerId]: true
    }));
    try {
      await testMyAIKey(providerId);
      reload();
    } catch (e) {
      alert(e.response?.data?.message || "Test failed");
    } finally {
      setTesting(t => ({
        ...t,
        [providerId]: false
      }));
    }
  };
  if (loadingP || loadingK) return <Box sx={{
    textAlign: "center",
    py: 6
  }}><CircularProgress /></Box>;
  const needsKey = p => p.type !== "ollama";
  return <Box>
      <Alert severity="info" sx={{
      mb: 3
    }}>{t("Your API keys are encrypted and stored securely. They are only used for AI calls made by your account.")}

      </Alert>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{
            bgcolor: "grey.50"
          }}>
              <TableCell sx={{
              fontWeight: 600
            }}>{t("Provider")}</TableCell>
              <TableCell sx={{
              fontWeight: 600
            }}>{t("Type")}</TableCell>
              <TableCell sx={{
              fontWeight: 600
            }}>{t("Key Status")}</TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} align="right">{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(providers || []).map(p => {
            const keyDoc = keyMap[p._id];
            return <TableRow key={p._id} hover>
                  <TableCell>
                    <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}>
                      <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                      {!needsKey(p) && <Chip label={t("No key needed")} size="small" color="info" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={p.type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75
                }}>
                      {keyDoc ? STATUS_ICON[keyDoc.testStatus] : null}
                      <Typography variant="caption" color="text.secondary">
                        {keyDoc ? `Key saved • ${keyDoc.testStatus === "ok" ? t("Verified") : keyDoc.testStatus === "failed" ? t("Failed") : t("Not tested")}` : needsKey(p) ? t("No key saved") : `Using ${p.baseUrl}`}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1
                }}>
                      {keyDoc && <Tooltip title={t("Test key")}>
                          <IconButton size="small" onClick={() => testKey(p._id)} disabled={!!testing[p._id]}>
                            {testing[p._id] ? <CircularProgress size={16} /> : <Play size={16} />}
                          </IconButton>
                        </Tooltip>}
                      {needsKey(p) && <Button size="small" variant="outlined" startIcon={<Key size={14} />} onClick={() => openKeyDialog(p)}>
                          {keyDoc ? t("Update") : t("Add Key")}
                        </Button>}
                      {keyDoc && <IconButton size="small" color="error" onClick={() => removeKey(p._id)}>
                          <Trash2 size={16} />
                        </IconButton>}
                    </Box>
                  </TableCell>
                </TableRow>;
          })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("API Key —")}{selectedProvider?.name}</DialogTitle>
        <DialogContent sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pt: 2
      }}>
          <TextField label={t("API Key")} value={keyValue} onChange={e => setKeyValue(e.target.value)} type={showKey ? "text" : "password"} fullWidth placeholder={t("Paste your API key here")} InputProps={{
          endAdornment: <IconButton onClick={() => setShowKey(!showKey)} edge="end">
                  {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
                </IconButton>
        }} />
          
          {(selectedProvider?.slug === "azure-openai" || selectedProvider?.slug === "openai-compatible") && <TextField label={t("Custom Base URL (optional)")} value={customBaseUrl} onChange={e => setCustomBaseUrl(e.target.value)} fullWidth placeholder={selectedProvider?.baseUrl} helperText={t("Override the default base URL for this provider")} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
          <Button variant="contained" onClick={saveKey} disabled={saving || !keyValue.trim()}>
            {saving ? <CircularProgress size={18} /> : t("Save Key")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
}

// ─── Agents Tab ───────────────────────────────────────────────────────────────

const AGENT_DEFAULTS = {
  name: "",
  description: "",
  providerId: "",
  model: "",
  systemPrompt: "",
  temperature: 0.2,
  maxTokens: 2048,
  topP: 1,
  isActive: true
};
function AgentsTab() {
  const {
    t
  } = useTranslation("ai_settings");
  const {
    data: agents,
    loading,
    reload
  } = useAsync(getAIAgents);
  const {
    data: providers
  } = useAsync(getAIProviders);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(AGENT_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(null);
  const [testPrompt, setTestPrompt] = useState("Say: hello");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const openNew = () => {
    setEditing(null);
    setForm(AGENT_DEFAULTS);
    setOpen(true);
  };
  const openEdit = a => {
    setEditing(a);
    setForm({
      name: a.name,
      description: a.description || "",
      providerId: a.providerId,
      model: a.model,
      systemPrompt: a.systemPrompt || "",
      temperature: a.temperature,
      maxTokens: a.maxTokens,
      topP: a.topP,
      isActive: a.isActive
    });
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      if (editing) await updateAIAgent(editing._id, form);else await createAIAgent(form);
      setOpen(false);
      reload();
    } catch (e) {
      alert(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const remove = async id => {
    if (!window.confirm(t("Delete this agent?"))) return;
    await deleteAIAgent(id);
    reload();
  };
  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await testAIAgent(testOpen._id, testPrompt);
      setTestResult({
        ok: true,
        reply: resp.data?.data?.reply
      });
    } catch (e) {
      setTestResult({
        ok: false,
        error: e.response?.data?.message || "Test failed"
      });
    } finally {
      setTesting(false);
    }
  };
  const selectedProvider = providers?.find(p => p._id === form.providerId);
  const availableModels = selectedProvider?.models || [];
  if (loading) return <Box sx={{
    textAlign: "center",
    py: 6
  }}><CircularProgress /></Box>;
  return <Box>
      <Box sx={{
      display: "flex",
      justifyContent: "space-between",
      mb: 3
    }}>
        <Typography variant="h6">{t("AI Agents")}</Typography>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={openNew}>{t("Create Agent")}</Button>
      </Box>

      {(!agents || agents.length === 0) && <Paper sx={{
      p: 4,
      textAlign: "center",
      color: "text.secondary"
    }}>
          <Cpu size={48} style={{
        marginBottom: 16,
        opacity: 0.4
      }} />
          <Typography>{t("No agents yet. Create one to get started.")}</Typography>
        </Paper>}

      <Grid container spacing={2}>
        {(agents || []).map(a => {
        const prov = providers?.find(p => p._id === a.providerId);
        return <Grid item xs={12} sm={6} md={4} key={a._id}>
              <Card variant="outlined" sx={{
            height: "100%",
            opacity: a.isActive ? 1 : 0.6
          }}>
                <CardContent>
                  <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1
              }}>
                    <Typography fontWeight={600}>{a.name}</Typography>
                    <Chip label={a.isActive ? "Active" : "Inactive"} size="small" color={a.isActive ? "success" : "default"} />
                  </Box>
                  {prov && <Chip label={prov.name} size="small" variant="outlined" sx={{
                mb: 1
              }} />}
                  <Typography variant="body2" color="text.secondary">{t("Model:")}{a.model}</Typography>
                  {a.description && <Typography variant="body2" color="text.secondary" sx={{
                mt: 0.5
              }}>{a.description}</Typography>}
                  <Box sx={{
                display: "flex",
                gap: 1,
                mt: 1.5,
                flexWrap: "wrap"
              }}>
                    <Chip label={`T: ${a.temperature}`} size="small" />
                    <Chip label={`Max: ${a.maxTokens} tokens`} size="small" />
                  </Box>
                </CardContent>
                <CardActions>
                  <IconButton size="small" onClick={() => openEdit(a)}><Edit2 size={16} /></IconButton>
                  <Tooltip title={t("Test agent")}>
                    <IconButton size="small" color="primary" onClick={() => {
                  setTestOpen(a);
                  setTestResult(null);
                  setTestPrompt("Say: hello");
                }}>
                      <Play size={16} />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" color="error" onClick={() => remove(a._id)}><Trash2 size={16} /></IconButton>
                </CardActions>
              </Card>
            </Grid>;
      })}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? t("Edit Agent") : t("Create Agent")}</DialogTitle>
        <DialogContent sx={{
        overflowX: "hidden"
      }}>
          <Grid container spacing={2} sx={{
          mt: 0
        }}>
            <Grid item xs={12} sm={6}>
              <TextField label={t("Agent Name")} value={form.name} onChange={e => setForm({
              ...form,
              name: e.target.value
            })} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Provider")}</InputLabel>
                <Select value={form.providerId} onChange={e => setForm({
                ...form,
                providerId: e.target.value,
                model: ""
              })} label={t("Provider")}>
                  {(providers || []).map(p => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Model")}</InputLabel>
                <Select value={form.model} onChange={e => setForm({
                ...form,
                model: e.target.value
              })} label={t("Model")}>
                  {availableModels.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                  <MenuItem value={form.model || "custom"}>{form.model || "Type custom model ID..."}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label={t("Custom Model ID")} value={form.model} onChange={e => setForm({
              ...form,
              model: e.target.value
            })} fullWidth placeholder={t("e.g. gpt-4o-mini")} />
            </Grid>
            <Grid item xs={12}>
              <TextField label={t("System Prompt")} value={form.systemPrompt} onChange={e => setForm({
              ...form,
              systemPrompt: e.target.value
            })} fullWidth multiline rows={4} placeholder={t("You are a helpful assistant...")} />
            </Grid>
            <Grid item xs={12}>
              <TextField label={t("Description")} value={form.description} onChange={e => setForm({
              ...form,
              description: e.target.value
            })} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography gutterBottom>{t("Temperature:")}{form.temperature}</Typography>
              <Slider value={form.temperature} onChange={(_, v) => setForm({
              ...form,
              temperature: v
            })} min={0} max={2} step={0.1} marks={[{
              value: 0,
              label: "0"
            }, {
              value: 1,
              label: "1"
            }, {
              value: 2,
              label: "2"
            }]} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label={t("Max Tokens")} type="number" value={form.maxTokens} onChange={e => setForm({
              ...form,
              maxTokens: Number(e.target.value)
            })} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography gutterBottom>{t("Top-P:")}{form.topP}</Typography>
              <Slider value={form.topP} onChange={(_, v) => setForm({
              ...form,
              topP: v
            })} min={0} max={1} step={0.05} marks={[{
              value: 0,
              label: "0"
            }, {
              value: 1,
              label: "1"
            }]} />
            </Grid>
            <Grid item xs={12} sm={6} sx={{
            display: "flex",
            alignItems: "center"
          }}>
              <FormControlLabel control={<Switch checked={form.isActive} onChange={e => setForm({
              ...form,
              isActive: e.target.checked
            })} />} label={t("Active")} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={!!testOpen} onClose={() => setTestOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("Test Agent:")}{testOpen?.name}</DialogTitle>
        <DialogContent sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pt: 2
      }}>
          <TextField label={t("Test Prompt")} value={testPrompt} onChange={e => setTestPrompt(e.target.value)} fullWidth multiline rows={3} />
          {testResult && <Alert severity={testResult.ok ? "success" : "error"}>
              {testResult.ok ? testResult.reply : testResult.error}
            </Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(null)}>{t("Close")}</Button>
          <Button variant="contained" onClick={runTest} disabled={testing} startIcon={testing ? <CircularProgress size={16} /> : <Zap size={16} />}>{t("Run Test")}

          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
}

// ─── Tasks Tab ────────────────────────────────────────────────────────────────

function TasksTab() {
  const {
    t
  } = useTranslation("ai_settings");
  const {
    data: tasks,
    loading,
    reload
  } = useAsync(getAITaskTypes);
  const {
    data: agents
  } = useAsync(getAIAgents);
  const [saving, setSaving] = useState({});
  const assign = async (slug, agentId) => {
    setSaving(s => ({
      ...s,
      [slug]: true
    }));
    try {
      await assignAIAgent(slug, agentId || null);
      reload();
    } catch (e) {
      alert(e.response?.data?.message || "Failed to assign");
    } finally {
      setSaving(s => ({
        ...s,
        [slug]: false
      }));
    }
  };
  if (loading) return <Box sx={{
    textAlign: "center",
    py: 6
  }}><CircularProgress /></Box>;
  return <Box>
      <Alert severity="info" sx={{
      mb: 3
    }}>{t("Assign an AI agent to each task type. When the app calls that task, it will use the assigned agent. Leave unassigned to disable AI for that task.")}

      </Alert>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{
            bgcolor: "grey.50"
          }}>
              <TableCell sx={{
              fontWeight: 600
            }}>{t("Task")}</TableCell>
              <TableCell sx={{
              fontWeight: 600
            }}>{t("Description")}</TableCell>
              <TableCell sx={{
              fontWeight: 600
            }}>{t("Status")}</TableCell>
              <TableCell sx={{
              fontWeight: 600,
              minWidth: 220
            }}>{t("Assigned Agent")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(tasks || []).map(task => <TableRow key={task._id} hover>
                <TableCell>
                  <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 1
              }}>
                    <Typography variant="body2" fontWeight={600}>{task.name}</Typography>
                    {task.isSeeded && <Chip label={t("Built-in")} size="small" color="primary" variant="outlined" />}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{task.description}</Typography>
                </TableCell>
                <TableCell>
                  {task.assignedAgentId ? <Chip label={t("Agent assigned")} size="small" color="success" /> : <Chip label={t("No agent")} size="small" color="default" />}
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t("Agent")}</InputLabel>
                    <Select value={task.assignedAgentId || ""} onChange={e => assign(task.slug, e.target.value)} label={t("Agent")} disabled={!!saving[task.slug]}>
                    
                      <MenuItem value=""><em>{t("None (disabled)")}</em></MenuItem>
                      {(agents || []).filter(a => a.isActive).map(a => <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>;
}

// ─── Main Component ───────────────────────────────────────────────────────────

const TABS = [{
  label: "Providers",
  icon: <Cpu size={16} />,
  component: ProvidersTab
}, {
  label: "My API Keys",
  icon: <Key size={16} />,
  component: MyKeysTab
}, {
  label: "Agents",
  icon: <Brain size={16} />,
  component: AgentsTab
}, {
  label: "Task Assignments",
  icon: <ListTodo size={16} />,
  component: TasksTab
}];
const AISettings = () => {
  const {
    t
  } = useTranslation("ai_settings");
  const [tab, setTab] = useState(0);
  const TabComponent = TABS[tab].component;
  return <Container maxWidth={false} className="AppContainer-root">
      <TitleBox title={t("AI Settings")} subTitle="Manage AI providers, API keys, agents, and task assignments" allowPrint={false} />
      
      <Paper elevation={1} sx={{
      borderRadius: 2,
      overflow: "hidden"
    }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "grey.50"
      }}>
          
          {TABS.map((t, i) => <Tab key={i} label={<Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75
        }}>{t.icon}{t.label}</Box>} />)}
        </Tabs>
        <Box sx={{
        p: 3
      }}>
          <TabComponent />
        </Box>
      </Paper>
    </Container>;
};
export default AISettings;
